import { createContext, memo, useContext, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import { logger } from '@lib/services/logger';
import remarkAiDirectives from './utils/remarkAiDirectives';
import {
  AiCitationRef,
  parseCitationPages,
  parseCitationTimes,
  resolveDocRefCitation,
} from './utils/citationPages';
import escapeDirectivePipes from './utils/escapeDirectivePipes';
import maskIncompleteDirective from './utils/maskIncompleteDirective';
import unwrapBacktickedDirectives from './utils/unwrapBacktickedDirectives';
import DocReference from './DocReference';
import styles from './AiAssistant.module.scss';

type Props = {
  content: string;
  // True while the assistant turn is still streaming — directives render as
  // placeholders and skip fetching until the message is persisted, and a
  // half-delivered trailing directive is hidden until its closing brace lands.
  isStreaming?: boolean;
  // Render markdown WITHOUT the `:documentation{id}` directive layer — the text
  // stays literal. Used for the Thoughts panel: reasoning is the model's own
  // monologue where it often quotes the chip syntax verbatim (e.g. "using
  // `:documentation{id=...}`"), and unwrapping/parsing those into live chips is
  // wrong there. The answer bubble keeps the directive layer (default false).
  plain?: boolean;
  // The message's verified citations (BE guide §5). A chip's `page`/`quote`
  // and `time` attributes are acted on only for the places listed here.
  citations?: AiCitationRef[];
};

// react-markdown renders to React elements (no dangerouslySetInnerHTML) and
// does NOT render raw HTML unless rehype-raw is added — so model output stays
// XSS-safe by default. Links are forced to open in a new tab with noopener.
//
// remark-directive parses `:documentation{id=x}` / `::documentation{id=x}` into
// directive nodes, remarkAiDirectives rewrites the known ones to the
// `ai-doc-ref` element, and the component map below renders them inline (chip)
// or as a block card.
const remarkPlugins = [remarkGfm, remarkDirective, remarkAiDirectives];
// Plain variant (Thoughts panel): GFM only, so `:documentation{id}` is left as
// literal text instead of being parsed into a directive node and swapped for a
// chip.
const plainRemarkPlugins = [remarkGfm];

// Carries the streaming flag to the (module-level) ai-doc-ref renderer without
// re-creating the components map on every render.
const StreamingContext = createContext(false);
// Carries the message's verified citations to the same renderer.
const CitationsContext = createContext<AiCitationRef[] | undefined>(undefined);

function MarkdownLink({
  children,
  // react-markdown passes the hast `node` to every component; pull it out of the
  // rest so it isn't forwarded onto the DOM <a> (React warns on the unknown
  // attribute). Intentionally unused — kept as a rest-sibling to strip it.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  node,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
  return (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

// Render paragraphs as a <div>, not <p>: an inline `:documentation{id}` chip can
// embed block-level content (its ActionsMenu renders <div>s), and a <div> inside
// a <p> is invalid HTML that React flags as a hydration error. A div paragraph
// carries the same block spacing via `.markdownParagraph`.
function MarkdownParagraph({
  children,
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <div className={styles.markdownParagraph}>{children}</div>;
}

// GFM tables come straight from the model, which picks both the column count and
// how verbose each cell is — so a table can easily want more width than the
// bubble has. Wrapping it in its own scroller lets a genuinely wide table scroll
// horizontally instead of having its columns crushed down to a couple of
// characters to fit (see `.markdownTableWrap` / the cell sizing rules).
function MarkdownTable({
  children,
  // Same as MarkdownLink: strip the hast `node` so it isn't forwarded to the DOM.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  node,
  ...props
}: React.TableHTMLAttributes<HTMLTableElement> & { node?: unknown }) {
  return (
    <div className={styles.markdownTableWrap}>
      <table {...props}>{children}</table>
    </div>
  );
}

function AiDocRef({
  id,
  versionId,
  page,
  quote,
  time,
  inline,
}: {
  id?: string;
  versionId?: string;
  page?: string;
  quote?: string;
  time?: string;
  inline?: boolean | string;
}) {
  const isStreaming = useContext(StreamingContext);
  const citations = useContext(CitationsContext);
  const resolved = useMemo(() => {
    if (!id) return null;
    const pages = parseCitationPages(page);
    const times = parseCitationTimes(time);
    const result = resolveDocRefCitation(
      { id, versionId, pages, quote, times },
      citations,
    );
    // Silent fallbacks to a plain chip: tell a model formatting slip apart
    // from a page the BE didn't verify. A streaming bubble has no citations
    // yet, so it logs nothing until the message is persisted.
    if (!isStreaming && page && !result.citation) {
      if (!pages.length) {
        logger.debug('AiAssistant: unparsable citation page', { id, page });
      } else {
        logger.debug('AiAssistant: citation page not verified', {
          id,
          versionId,
          page,
          citationCount: citations?.length ?? 0,
        });
      }
    }
    if (!isStreaming && time && !result.moments) {
      logger.debug('AiAssistant: citation time not verified', {
        id,
        versionId,
        time,
        citationCount: citations?.length ?? 0,
      });
    }
    return result;
  }, [citations, id, isStreaming, page, quote, time, versionId]);
  return (
    <DocReference
      id={id}
      versionId={resolved?.versionId}
      citation={resolved?.citation}
      moments={resolved?.moments}
      inline={inline === true || inline === 'true'}
      isStreaming={isStreaming}
    />
  );
}

const components = {
  a: MarkdownLink,
  p: MarkdownParagraph,
  table: MarkdownTable,
  'ai-doc-ref': AiDocRef,
} as Components;

function MessageMarkdown({
  content,
  isStreaming = false,
  plain = false,
  citations,
}: Props) {
  // Plain mode leaves directive syntax untouched (no unwrap, no masking) so it
  // renders as literal text. Otherwise: un-code-format directives the model
  // backticked before anything parses them, escape `|` inside directives so a
  // quote can't split a table cell, then (while streaming) hide a trailing
  // half-delivered one.
  let markdown = content;
  if (!plain) {
    const prepared = escapeDirectivePipes(unwrapBacktickedDirectives(content));
    markdown = isStreaming ? maskIncompleteDirective(prepared) : prepared;
  }
  return (
    <div className={styles.markdown}>
      <StreamingContext.Provider value={isStreaming}>
        <CitationsContext.Provider value={citations}>
          <ReactMarkdown
            remarkPlugins={plain ? plainRemarkPlugins : remarkPlugins}
            components={components}
          >
            {markdown}
          </ReactMarkdown>
        </CitationsContext.Provider>
      </StreamingContext.Provider>
    </div>
  );
}

// react-markdown runs a full parse + mdast→hast + React reconciliation on every
// render with no internal memoization, so without this every settled message in
// the transcript re-parses its markdown on every streamed delta of the current
// turn (O(messages) parses per frame). Props are primitives plus the message's
// `citations` array, so the default shallow compare is exactly right: a settled
// bubble bails out, and the one streaming bubble still re-renders because its
// `content` grows each frame. Callers must pass `message.citations` straight
// through — an array rebuilt per render would defeat the memo.
export default memo(MessageMarkdown);
